import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { TrendingUp, Sparkles, ArrowRight, Globe } from "lucide-react";
import QuillMascot from "@/components/QuillMascot";

const TIER_BADGE = {
  basic: { label: "Foundation", cls: "bg-[#00FF9D]/15 text-[#00FF9D] border-[#00FF9D]/30" },
  intermediate: { label: "Practitioner", cls: "bg-[#0044FF]/15 text-[#8aa8ff] border-[#0044FF]/30" },
  expert: { label: "Expert", cls: "bg-[#5D3FD3]/15 text-[#b9a8ff] border-[#5D3FD3]/30" },
};

// Quill's contextual line for the trending row. Persona vs global are
// distinct buckets so the mascot's voice adapts to the data state without
// repeating the same canned line. Pulled from the central `quillLines`
// bank so all surfaces stay in voice.
import { pickQuillLine as pickFromBank } from "@/lib/quillLines";

function pickQuillLine({ isPersona, totalSignals, topGoal }) {
  if (totalSignals <= 0) return pickFromBank("empty_dashboard");
  // For non-zero signals we want context (the top goal) injected, so we
  // keep the small local pool here rather than the generic bank.
  const truncated = (topGoal || "").length > 40 ? topGoal.slice(0, 40) + "…" : topGoal;
  const personaLines = [
    `${totalSignals} learners like you dove in this fortnight. Here's what stuck.`,
    `Spotted ${totalSignals} fresh sparks in your cohort — top pick: "${truncated}".`,
    `Your cohort's been busy — ${totalSignals} signals on these in the last 14 days.`,
  ];
  const globalLines = [
    `${totalSignals} explorers leaned in this fortnight. Borrow a topic.`,
    `Top of the OAK pulse: "${truncated}". Want to try it?`,
    `Hot off the press — ${totalSignals} learners chose these in 14 days.`,
  ];
  const pool = isPersona ? personaLines : globalLines;
  return pool[Math.floor(Math.random() * pool.length)];
}

/**
 * TrendingForYou — surfaces the hottest goals over the last 14 days from
 * (a) the user's persona cohort (same domain/seniority) when ≥5 peers
 *     exist, or (b) the global pool as fallback.
 *
 * Quill (our oak-sprite mascot) introduces the row with a contextual
 * speech bubble — replaces the privacy-leaking idea of avatar stacks
 * with a brand-owned, anonymous social-proof signal.
 *
 * Self-hides when no trending data is found (cold-start safe).
 */
export default function TrendingForYou() {
  const [state, setState] = useState({ loading: true, items: [], cohort: null });

  useEffect(() => {
    let cancelled = false;
    api.get("/trending/for-you")
      .then(({ data }) => {
        if (cancelled) return;
        setState({
          loading: false,
          items: data?.trending || [],
          cohort: data?.cohort || "global",
          cohortSize: data?.cohort_size || 0,
          signals: data?.match_signals || {},
        });
      })
      .catch(() => { if (!cancelled) setState({ loading: false, items: [] }); });
    return () => { cancelled = true; };
  }, []);

  if (state.loading || state.items.length === 0) return null;

  const isPersona = state.cohort === "persona";
  const totalSignals = state.items.reduce((acc, it) => acc + it.score, 0);
  const topGoal = state.items[0]?.goal;
  const quillLine = pickQuillLine({ isPersona, totalSignals, topGoal });

  return (
    <section className="space-y-4" data-testid="trending-for-you">
      {/* Quill intro — replaces avatar stack social proof. No PII, just
          aggregated intent counts spoken by our mascot. */}
      <div className="flex items-start gap-4 flex-wrap" data-testid="quill-intro">
        <QuillMascot size="md" testid="trending-quill" />
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <h3 className="font-display text-base md:text-lg text-white tracking-tight">
              {isPersona ? "What's hot in your world" : "What's trending right now"}
            </h3>
            <span
              className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border font-mono-oak ${
                isPersona
                  ? "bg-[#FF7B00]/10 text-[#ffb067] border-[#FF7B00]/30"
                  : "bg-white/[0.04] text-white/55 border-white/10"
              }`}
              data-testid="trending-cohort-badge"
            >
              {isPersona ? `${state.cohortSize} like you` : <span className="inline-flex items-center gap-1"><Globe size={9} />global</span>}
            </span>
            {!isPersona && (
              <Link
                to="/settings"
                className="ml-auto text-[11px] text-white/45 hover:text-white transition inline-flex items-center gap-1"
                data-testid="trending-tune"
                title="Set your domain & seniority to personalize this row"
              >
                <Sparkles size={11} /> Personalize
              </Link>
            )}
          </div>
          <div
            className="quill-speech"
            data-testid="quill-speech"
            style={{ background: "rgba(11,14,20,0.6)", borderColor: "rgba(0,255,157,0.22)" }}
          >
            <span className="quill-speech-name">Quill</span>
            <span className="quill-speech-body">{quillLine}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="trending-grid">
        {state.items.slice(0, 6).map((it, i) => {
          const tier = TIER_BADGE[it.difficulty_tier] || TIER_BADGE.intermediate;
          return (
            <article
              key={(it.sample_path_id || "k") + i}
              className="rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-[#FF7B00]/40 transition p-4 flex flex-col gap-3 animate-fadeup"
              style={{ animationDelay: `${i * 60}ms` }}
              data-testid={`trending-card-${i}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border font-mono-oak ${tier.cls}`}>
                  {tier.label}
                </span>
                <span className="text-[10px] text-white/45 inline-flex items-center gap-1 font-mono-oak">
                  <TrendingUp size={9} className="text-[#FF7B00]" />
                  {it.generations + it.saves} signal{(it.generations + it.saves) === 1 ? "" : "s"}
                </span>
              </div>
              <div className="font-display text-[15px] text-white leading-snug min-h-[44px]">
                {it.goal}
              </div>
              {it.summary && (
                <p className="text-[12px] text-white/55 leading-relaxed line-clamp-2">{it.summary}</p>
              )}
              <Link
                to={it.sample_path_id ? `/path/${it.sample_path_id}` : "/academy"}
                className="mt-auto inline-flex items-center gap-1.5 text-xs text-[#ffb067] hover:text-white transition"
                data-testid={`trending-open-${i}`}
              >
                Explore this topic <ArrowRight size={12} />
              </Link>
            </article>
          );
        })}
      </div>
    </section>
  );
}
